import { useEffect, useState, type ReactNode } from 'react';

import { IconButton, SelectField, Toggle, VecField } from '../../ui/controls.js';
import { CurveCanvas } from './CurveCanvas.js';
import { movePoint, tidyPoint, type Vec2 } from './curve-math.js';
import { usePointDraft } from './use-point-draft.js';
import './curves.css';

export const GRID_SIZES = ['0.005', '0.01', '0.025', '0.05', '0.1', '0.25', '0.5', '1'];

export const CANVAS_HELP =
  'Click: add point · click the curve: insert · drag: move (Shift = one axis, Ctrl = toggle snap) · double-click / Del: delete · wheel: zoom · right/middle drag: pan · F: frame';

/** Snap / grid / frame controls shared by the profile and path editors. */
export function CanvasTools({
  snap,
  grid,
  onSnap,
  onGrid,
  onFrame,
  children,
}: {
  snap: boolean;
  grid: number;
  onSnap: (on: boolean) => void;
  onGrid: (g: number) => void;
  onFrame: () => void;
  children?: ReactNode;
}) {
  return (
    <div className="curve-tools">
      {children}
      <span className="curve-tools-gap" />
      <IconButton icon="magnet" title="Snap to grid (hold Ctrl to invert)" size={13} active={snap} onClick={() => onSnap(!snap)} />
      <SelectField value={String(grid)} options={GRID_SIZES} onChange={(g) => onGrid(Number(g))} />
      <IconButton icon="focus" title="Frame all (F)" size={13} onClick={onFrame} />
    </div>
  );
}

export interface ProfileEditorProps {
  points: readonly Vec2[];
  onChange: (points: Vec2[]) => void;
  /** Closed outline (extrude, sweep / loft profiles, cloth) vs open profile (lathe). */
  closed?: boolean;
  smooth?: boolean;
  samples?: number;
  /** Shows a Smooth toggle bound to the shape's own field. */
  onSmoothChange?: (smooth: boolean) => void;
  /** Lathe profile: x is the radius (kept ≥ 0) revolved around +Y; the mirrored silhouette is drawn. */
  lathe?: boolean;
  flipY?: boolean;
  axes?: [string, string];
  ghosts?: readonly { points: readonly Vec2[]; closed?: boolean }[];
  height?: number;
}

/**
 * 2D profile canvas: draw and edit the points of a lathe profile, extrude /
 * sweep / loft outline or cloth pattern piece, previewing the curve exactly
 * as prism smooths it, with numeric editing of the selected point.
 */
export function ProfileEditor({
  points,
  onChange,
  closed = false,
  smooth = false,
  samples = 6,
  onSmoothChange,
  lathe = false,
  flipY = false,
  axes = lathe ? ['radius', 'Y'] : ['X', 'Y'],
  ghosts,
  height = 220,
}: ProfileEditorProps) {
  const [selected, setSelected] = useState<number | null>(null);
  const [snap, setSnap] = useState(false);
  const [grid, setGrid] = useState(0.05);
  const [frame, setFrame] = useState(0);
  const draft = usePointDraft<Vec2>(points, (next) => onChange(next.map(tidyPoint)));
  const sel = selected !== null ? draft.points[selected] : undefined;
  const clamp = (p: Vec2): Vec2 => (lathe ? [Math.max(0, p[0]), p[1]] : p);
  const [asText, setAsText] = useState(false);

  return (
    <div className="curve-editor">
      <CanvasTools snap={snap} grid={grid} onSnap={setSnap} onGrid={setGrid} onFrame={() => setFrame((f) => f + 1)}>
        {onSmoothChange && <Toggle label="Smooth" checked={smooth} onChange={onSmoothChange} />}
        <span className="curve-count muted">{points.length} pts</span>
        <IconButton icon="code" title="Edit the points as text (one x, y per line)" size={13} active={asText} onClick={() => setAsText(!asText)} />
        {lathe && points.length >= 2 && (
          <IconButton
            icon="target"
            title="Close the ends on the axis (first and last radius = 0)"
            size={13}
            onClick={() => {
              const next = points.slice();
              next[0] = [0, next[0]![1]];
              next[next.length - 1] = [0, next[next.length - 1]![1]];
              onChange(next);
            }}
          />
        )}
      </CanvasTools>
      {asText && <PointsText points={points} onApply={(next) => onChange(next.map((p) => tidyPoint(clamp(p))))} />}
      <CurveCanvas
        points={draft.points}
        closed={closed}
        smooth={smooth}
        samples={samples}
        selected={selected !== null && selected < draft.points.length ? selected : null}
        onSelect={setSelected}
        onMove={(i, p) => draft.move(i, clamp(p))}
        onCommit={draft.finish}
        onAdd={(p) => draft.add(clamp(p))}
        onInsert={(segment, p) => draft.add(clamp(p), segment + 1)}
        onDelete={(i) => {
          draft.remove(i);
          setSelected(null);
        }}
        grid={grid}
        snap={snap}
        minX={lathe ? 0 : undefined}
        mirrorX={lathe}
        flipY={flipY}
        axes={axes}
        ghosts={ghosts}
        height={height}
        frameKey={String(frame)}
      />
      <div className="curve-point-row">
        {sel && selected !== null ? (
          <>
            <span className="curve-point-index mono">#{selected}</span>
            <VecField
              size={2}
              step={0.01}
              labels={axes.map((a) => a.slice(0, 1).toUpperCase())}
              value={sel}
              onChange={(v) => onChange(movePoint(points, selected, tidyPoint(clamp([v[0] ?? 0, v[1] ?? 0]))))}
            />
            <IconButton
              icon="trash"
              title="Delete point (Del)"
              size={13}
              onClick={() => {
                draft.remove(selected);
                setSelected(null);
              }}
            />
          </>
        ) : (
          <span className="muted curve-help" title={CANVAS_HELP}>
            {points.length ? 'Select a point to edit it numerically.' : 'Click in the canvas to place the first point.'}
          </span>
        )}
      </div>
    </div>
  );
}

/** Format points as `x, y` lines. */
export function pointsToText(points: readonly Vec2[]): string {
  const f = (n: number) => String(Math.round(n * 10000) / 10000);
  return points.map((p) => `${f(p[0])}, ${f(p[1])}`).join('\n');
}

/** Parse `x, y` (or `x y`, `[x, y]`) lines; null when any line is not a point. */
export function parsePoints(text: string): Vec2[] | null {
  const out: Vec2[] = [];
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.replace(/[[\]()]/g, ' ').trim();
    if (!line) continue;
    const nums = line.split(/[\s,;]+/).filter(Boolean).map(Number);
    if (nums.length !== 2 || nums.some((n) => !Number.isFinite(n))) return null;
    out.push([nums[0]!, nums[1]!]);
  }
  return out;
}

/** Text box with the points; Apply (or Ctrl+Enter) replaces them all. */
function PointsText({ points, onApply }: { points: readonly Vec2[]; onApply: (points: Vec2[]) => void }) {
  const [text, setText] = useState(() => pointsToText(points));
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setText(pointsToText(points)), [points]);
  const apply = () => {
    const parsed = parsePoints(text);
    if (!parsed || parsed.length < 2) {
      setError('Each line needs two numbers: x, y (at least 2 points).');
      return;
    }
    setError(null);
    onApply(parsed);
  };
  return (
    <div className="curve-text">
      <textarea
        className="mono"
        spellCheck={false}
        rows={Math.min(12, Math.max(4, points.length + 1))}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            apply();
          }
        }}
        aria-label="Points, one x, y per line"
      />
      <div className="curve-text-row">
        {error ? <span className="inline-error">{error}</span> : <span className="muted">One point per line · Ctrl+Enter applies</span>}
        <button type="button" className="ghost small" onClick={apply}>
          Apply
        </button>
      </div>
    </div>
  );
}
